import React, { useEffect, useRef, useState } from "react";
import Header from "../common/Header";
import "../../_css/EachStudentReport.css";
import { useNavigate, useSearchParams } from "react-router-dom";
import Feedback from "../UI/Feedback";
import {
  addTeacherRemarks,
  createPDFFeedback,
  createPDFFeedbackWf,
  createPDFStudent,
  getStudentReport,
  sharePDF,
} from "../../requests";
import { useDispatch } from "react-redux";
import { startLoader, stopLoader } from "../../_store/reducers/loaderReducer";
import { deleteAuthToken, notifyError, notifySuccess } from "../../utils/helper";
import { PencilIcon } from "lucide-react";
import html2pdf from "html2pdf.js";
import { getPercentage, getRemarks } from "../helper/formatters";
import ReviewAnsPopUp from "../UI/ReviewPopUp";
import ValidationQualityIndicator from "../common/ValidationQualityIndicator";


const LIST_PATH = "/student-reports/students/list";

const buildListUrl = (row) => {
  const q = new URLSearchParams({
    grade: String(row.grade ?? ""),
    exam: String(row.exam_name ?? ""),
    student_name: String(row.student_name ?? ""),
  });
  return `${LIST_PATH}?${q.toString()}`;
};

const EachStudentReport = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const grade = searchParams.get("grade") ?? "";
  const exam = searchParams.get("exam") ?? "";
  const queryStudentName = searchParams.get("student_name") ?? "";
  const [showFeedback, setShowFeedback] = useState(false);
  const [student, setStudent] = useState(null);
  const [allstudents, setAllStudents] = useState([]);
  const [prevData, setPrevData] = useState(null);
  const [nextData, setNextData] = useState(null);
  const [id, setId] = useState(null);
  const [pdf, setPdf] = useState(null);
  const [showReviewPopUp, setShowReviewPopUp] = useState(false);
  const [prevBtn, setPrevBtn] = useState(true);
  const [nextBtn, setNextBtn] = useState(true);
  const [teacherRemarks, setTeacherRemarks] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [selectedPaperIds, setSelectedPaperIds] = useState(() => new Set());

  //edit teacher remarks ref
  const editTeacherRemarksRef = useRef(null);

  // Download PDF
  const divRef = useRef(null);

  const handleDownloadPDF = async (dwld) => {
    const element = divRef.current;

    // Optional: hide elements you don't want in PDF
    const excludeElements = element.querySelectorAll("#exclude-pdf");
    excludeElements.forEach((el) => (el.style.display = "none"));

    // Generate PDF using html2pdf
    const options = {
      margin: 0.5,
      filename: "report.pdf",
      html2canvas: { scale: 2, letterRendering: true },
      jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
    };

    await html2pdf()
      .set(options)
      .from(element)
      .toPdf()
      .get("pdf")
      .then((pdf) => {
        // Convert PDF to Blob
        const blob = pdf.output("blob");
        setPdf(blob);

        //download automatically
        dwld && pdf.save(`${student?.subjects[0]?.student_name}-report.pdf`);
      });

    // Restore hidden elements
    excludeElements.forEach((el) => (el.style.display = ""));
  };

  /** Feedback PDF blob — merged with AI marked sheet when workflow PDF exists (see Feedback.js). */
  const getFeedbackPdfBlob = async (subjectId, useWfMarked) => {
    if (useWfMarked) {
      const response = await createPDFFeedbackWf({ id: subjectId });
      return new Blob([response.data], { type: "application/pdf" });
    }
    const response = await createPDFFeedback({ id: subjectId });
    return new Blob([response.data], { type: "application/pdf" });
  };

  const togglePaperSelection = (paperId) => {
    setSelectedPaperIds((prev) => {
      const next = new Set(prev);
      if (next.has(paperId)) {
        next.delete(paperId);
      } else {
        next.add(paperId);
      }
      return next;
    });
  };

  const handleToggleAllPapers = () => {
    const ids = student?.subjects?.map((s) => s.id) ?? [];
    if (!ids.length) return;
    const allSelected = ids.every((id) => selectedPaperIds.has(id));
    setSelectedPaperIds(allSelected ? new Set() : new Set(ids));
  };

  const handleSharePDF = async () => {
    const subjects = student?.subjects;
    if (!subjects?.length) {
      notifyError("No feedback available to share.");
      return;
    }

    const subjectsToShare = subjects.filter((subj) =>
      selectedPaperIds.has(subj.id)
    );
    if (!subjectsToShare.length) {
      notifyError("Please select at least one paper to share.");
      return;
    }

    dispatch(startLoader());
    try {
      let sharedCount = 0;

      for (const subj of subjectsToShare) {
        const hasWfMarked = Boolean(
          subj.file_wf_marked && String(subj.file_wf_marked).trim()
        );
        let pdfBlob;
        try {
          pdfBlob = await getFeedbackPdfBlob(subj.id, hasWfMarked);
        } catch (err) {
          if (hasWfMarked) {
            pdfBlob = await getFeedbackPdfBlob(subj.id, false);
          } else {
            throw err;
          }
        }

        const safeName = (subj.student_name || queryStudentName || "student").replace(
          /[/\\?%*:|"<>]/g,
          "-"
        );
        const safeSubject = (subj.subject || "subject").replace(
          /[/\\?%*:|"<>]/g,
          "-"
        );
        const filename = hasWfMarked
          ? `${safeName}_${safeSubject}_feedback_with_marked_answer_sheet.pdf`
          : `${safeName}_${safeSubject}_feedback.pdf`;

        const pdfFile = new File([pdfBlob], filename, {
          type: "application/pdf",
        });

        const formData = new FormData();
        formData.append("pdf", pdfFile);
        formData.append("grade", String(subj.grade ?? grade));
        formData.append("student_name", subj.student_name || queryStudentName);
        formData.append("subject", subj.subject);
        formData.append("type", 1);

        const res = await sharePDF(formData);
        if (res.status < 400) {
          sharedCount += 1;
        }
      }

      if (sharedCount > 0) {
        notifySuccess(
          sharedCount === 1
            ? "Feedback report sent to your registered email successfully"
            : `${sharedCount} feedback reports sent to your registered email successfully`
        );
      }
    } catch (error) {
      notifyError(
        error?.response?.data?.error || "Server busy please try again.."
      );
    } finally {
      dispatch(stopLoader());
    }
  };

  const get_students = async () => {
    if (!grade || !exam || !queryStudentName) {
      setStudent(null);
      setAllStudents([]);
      setTeacherRemarks("");
      return;
    }
    dispatch(startLoader());
    try {
      const res = await getStudentReport({
        grade,
        student_name: queryStudentName,
        exam,
      });

      const allRes = await getStudentReport(
        {},
        {
          page: 1,
          limit: 500,
          grade,
          exam_name: exam,
        }
      );

      const first = res.data?.subjects?.[0];
      setTeacherRemarks(first?.teacher_remark ?? "");

      setStudent(res.data);
      setAllStudents(Array.isArray(allRes.data?.data) ? allRes.data.data : []);
    } catch (error) {
      if (error?.response?.status == 403) {
        notifyError(
          error?.response?.data?.error ||
            "Your session has expired. Please log in again."
        );
        deleteAuthToken();
        navigate("/login");
        return;
      }

      notifyError("Error fetching students");
    } finally {
      dispatch(stopLoader());
    }
  };

  const paging = () => {
    if (!allstudents.length || !student?.subjects?.[0]) {
      setPrevBtn(false);
      setNextBtn(false);
      setPrevData(null);
      setNextData(null);
      return;
    }

    const curName = student.subjects[0].student_name;
    const curGrade = student.subjects[0].grade;
    const curExam = student.subjects[0].exam_name;

    const currentIndex = allstudents.findIndex(
      (e) =>
        e.student_name === curName &&
        String(e.grade) === String(curGrade) &&
        e.exam_name === curExam
    );

    if (currentIndex < 0) {
      setPrevBtn(false);
      setNextBtn(false);
      setPrevData(null);
      setNextData(null);
      return;
    }

    if (currentIndex > 0) {
      setPrevData(allstudents[currentIndex - 1]);
      setPrevBtn(true);
    } else {
      setPrevData(allstudents[0]);
      setPrevBtn(false);
    }

    if (currentIndex < allstudents.length - 1) {
      setNextData(allstudents[currentIndex + 1]);
      setNextBtn(true);
    } else {
      setNextData(allstudents[currentIndex]);
      setNextBtn(false);
    }
  };

  const handlepagination = (num) => {
    try {
      if (num === 0 && prevData) {
        navigate(buildListUrl(prevData));
      }
      if (num === 1 && nextData) {
        navigate(buildListUrl(nextData));
      }
    } catch (error) {
      notifyError("Something went wrong. Please try again.");
      console.log(error);
    }
  };

  const handleDownloadPDFStudent = async (share = false) => {
    const subjects = student?.subjects;
    if (!subjects?.length) {
      notifyError("No feedback available to share.");
      return;
    }

    const subjectsToShare = subjects.filter((subj) =>
      selectedPaperIds.has(subj.id)
    );
    if (!subjectsToShare.length) {
      notifyError("Please select at least one paper to share.");
      return;
    }
    dispatch(startLoader());
    let sharedCount = 0;
    try {

      for (const subj of subjectsToShare) {
        const hasWfMarked = Boolean(
          subj.file_wf_marked && String(subj.file_wf_marked).trim()
        );
        let pdfBlob;
        try {
          pdfBlob = await getFeedbackPdfBlob(subj.id, hasWfMarked);
        } catch (err) {
          if (hasWfMarked) {
            pdfBlob = await getFeedbackPdfBlob(subj.id, false);
          } else {
            throw err;
          }
        }

        const safeName = (subj.student_name || queryStudentName || "student").replace(
          /[/\\?%*:|"<>]/g,
          "-"
        );
        const safeSubject = (subj.subject || "subject").replace(
          /[/\\?%*:|"<>]/g,
          "-"
        );
        const filename = hasWfMarked
          ? `${safeName}_${safeSubject}_feedback_with_marked_answer_sheet.pdf`
          : `${safeName}_${safeSubject}_feedback.pdf`;

        const pdfFile = new File([pdfBlob], filename, {
          type: "application/pdf",
        });

    const url = window.URL.createObjectURL(pdfFile);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${student?.subjects[0]?.student_name}_report.pdf`;
        document.body.appendChild(a);
        a.click();

      }
      // const res = await createPDFStudent({
      //   grade,
      //   student_name: queryStudentName,
      //   exam,
      // });
      // const file = new Blob([res.data], { type: "application/pdf" });
      // if (share) {
      //   return file;
      // } else {
      //   const url = window.URL.createObjectURL(file);
      //   const a = document.createElement("a");
      //   a.href = url;
      //   a.download = `${student?.subjects[0]?.student_name}_report.pdf`;
      //   document.body.appendChild(a);
      //   a.click();
      // }
    } catch (error) {
      notifyError(error?.response?.data?.error || "Something went wrong");
    } finally {
      dispatch(stopLoader());
    }
  };
  const handleDownLoadWord = async () => {
    dispatch(startLoader());
    try {
      const res = await createPDFStudent({
        grade,
        student_name: queryStudentName,
        exam,
        type: 1,
      });
      const file = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });
      const url = window.URL.createObjectURL(file);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${student?.subjects[0]?.student_name}_report.docx`;
      document.body.appendChild(a);
      a.click();
    } catch (error) {
      notifyError(error?.response?.data?.error || "Something went wrong");
    } finally {
      dispatch(stopLoader());
    }
  };

  const handleAddTeacherRemarks = async () => {
    if (!teacherRemarks) {
      notifyError("Please enter teacher remarks");
      return;
    }
    try {
      dispatch(startLoader());
      const res = await addTeacherRemarks({
        student_name: queryStudentName,
        grade,
        exam,
        remarks: teacherRemarks,
      });
      if (res.status < 400) {
        notifySuccess("Teacher remarks added successfully");
        setIsEditing(false);
      }
    } catch (error) {
      notifyError(error?.response?.data?.error || "Something went wrong");
    } finally {
      dispatch(stopLoader());
    }
  };

  useEffect(() => {
    get_students();
  }, [grade, exam, queryStudentName, showReviewPopUp]);

  useEffect(() => {
    paging();
  }, [student, allstudents]);

  useEffect(() => {
    const ids = student?.subjects?.map((s) => s.id) ?? [];
    setSelectedPaperIds(new Set(ids));
  }, [student?.subjects]);

  return (
    <div className="page-main">
        
     
      {showReviewPopUp && (
        <ReviewAnsPopUp id={id} fn={() => setShowReviewPopUp(false)} />
      )}
      <Header name={["Student Report", "Students", "Student Report Details"]} params={{grade, exam, queryStudentName}} />
      {showFeedback && (
        <Feedback fn={() => setShowFeedback(false)} id={id} save={false} />
      )}

      {(!grade || !exam || !queryStudentName) && (
        <div className="ai-fd-main" style={{ padding: 24 }}>
          <p className="ai-fb-context-muted" style={{ margin: 0 }}>
            Missing grade, exam, or student. Open a student from Student Reports
            → View students.
          </p>
        </div>
      )}

      {grade && exam && queryStudentName && (
      <div className="each-stu-main" ref={divRef}>
        {/* Validation Quality Indicator - NEW */}
        {/* {student?.subjects && student.subjects[0]?.validation_flags && (
          <ValidationQualityIndicator
            validationFlags={student.subjects[0].validation_flags}
          />
        )} */}
        
        <div className="each-stu-head">
          <div className="each-stu-head-top">
            <div className="each-stu-t-left">
              <div
                className="each-stu-txt"
                style={{ width: "200px", textAlign: "left" }}
              >
                {student?.subjects[0]?.student_name}
                {" "}
               
              </div>
              <div className="each-stu-arrow" id="exclude-pdf">
                {/* <img
                  onClick={() => handlepagination(0)}
                  className="pe-cursor"
                  src="/nav-arrow-left.svg"
                  height="18px"
                  width="18px"
                /> */}
                <button
                  className="my-btn"
                  style={{
                    backgroundColor: "#E0F5FE",
                    color: "#01A7DE",
                    disabled: !prevBtn,
                    opacity: !prevBtn ? 0.5 : 1,
                  }}
                  onClick={() => handlepagination(0)}
                  disabled={!prevBtn}
                >
                  Previous Student
                </button>
                <button
                  className="my-btn"
                  style={{
                    backgroundColor: "#E0F5FE",
                    color: "#01A7DE",
                    disabled: !nextBtn,
                    opacity: !nextBtn ? 0.5 : 1,
                  }}
                  onClick={() => handlepagination(1)}
                  disabled={!nextBtn}
                >
                  Next Student
                </button>

                {/* <img
                  onClick={() => handlepagination(1)}
                  className="pe-cursor"
                  src="/nav-arrow-right.svg"
                  height="18px"
                  width="18px"
                /> */}
              </div>
            </div>
            <div className="each-stu-t-right" id="exclude-pdf">
              <button
                className="each-stu-head-btn"
                onClick={() => handleDownloadPDFStudent()}
              >
                Download pdf
              </button>
              {/* <button
                className="each-stu-head-btn"
                onClick={handleDownLoadWord}
              >
                Download word
              </button> */}
              <button
                className="each-stu-head-btn"
                style={{
                  backgroundColor: "#01A7DE",
                  color: "#FFFFFF",
                  opacity: selectedPaperIds.size === 0 ? 0.5 : 1,
                }}
                onClick={handleSharePDF}
                disabled={selectedPaperIds.size === 0}
              >
                Share
              </button>
            </div>
          </div>
          <div className="each-stu-head-bottom">
          <div> Roll No - {student?.subjects[0]?.student_rollno}</div>
            <div>Class - {grade}</div>
            <div> Exam - {exam}</div>
            
          </div>
        </div>
        <div className="each-stu-table">
          <div className="each-stu-table-head">
            <div
              className="each-stu-head-cell each-stu-mail-cell"
              style={{ width: "5%" }}
              title="Select papers to email"
            >
              <input
                type="checkbox"
                className="each-stu-mail-checkbox"
                checked={
                  (student?.subjects?.length ?? 0) > 0 &&
                  student.subjects.every((s) => selectedPaperIds.has(s.id))
                }
                onChange={handleToggleAllPapers}
                aria-label="Select all papers for email"
              />
            </div>
            <div className="each-stu-head-cell" style={{ width: "19%" }}>
              Subject
            </div>
            <div className="each-stu-head-cell" style={{ width: "19%" }}>
              Marks
            </div>
            <div className="each-stu-head-cell" style={{ width: "19%" }}>
              Marks Obtained
            </div>
            <div className="each-stu-head-cell" style={{ width: "19%" }}>
              Percentage Obtained
            </div>
            <div
              className="each-stu-head-cell pe-cursor"
              style={{ width: "19%" }}
            >
              Actions
            </div>
          </div>
          {student?.subjects?.length > 0 &&
            student.subjects.map((e) => (
              <div key={e.id} className="each-stu-table-row">
                <div
                  className="each-stu-row-cell each-stu-mail-cell"
                  style={{ width: "5%" }}
                >
                  <input
                    type="checkbox"
                    className="each-stu-mail-checkbox"
                    checked={selectedPaperIds.has(e.id)}
                    onChange={() => togglePaperSelection(e.id)}
                    aria-label={`Include ${e.subject} in email`}
                  />
                </div>
                <div className="each-stu-row-cell" style={{ width: "19%" }}>
                  {e.subject}
                </div>
                <div className="each-stu-row-cell" style={{ width: "19%" }}>
                  {Number(e.marks_total)}
                </div>
                <div className="each-stu-row-cell" style={{ width: "19%" }}>
                  {Number(e.marks_obt)}
                </div>
                <div className="each-stu-row-cell" style={{ width: "19%" }}>
                  {e.percentage != null && !Number.isNaN(Number(e.percentage))
                    ? Number(e.percentage).toFixed(2) + "%"
                    : getPercentage(e.marks_obt, e.marks_total)}
                </div>
                <div
                  className="each-stu-row-cell pe-cursor"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "start",
                    gap: "12px",
                    width: "19%",
                  }}
                >
                  <button
                    onClick={() => (setShowFeedback(true), setId(e.id))}
                    className="ai-fd-fb-btn"
                  >
                    feedback
                  </button>

                  <button
                    style={{ backgroundColor: "#E0F5FE", color: "#01A7DE" }}
                    className="ai-fd-fb-btn"
                    onClick={() => (setShowReviewPopUp(true), setId(e.id))}
                  >
                    View
                  </button>
                </div>
              </div>
            ))}
        </div>
        <div className="each-stu-report">
          <div className="each-stu-report-box">
            <div className="each-stu-r-b-top">
              <div className="each-stu-report-txt">Total marks</div>
              <div className="each-stu-report-txt">
                {Number(student?.total_marks)}
              </div>
            </div>
            <div className="each-stu-r-b-top">
              <div>Obtained marks</div>
              <div>{Number(student?.obt_marks)}</div>
            </div>
          </div>
          <div className="each-stu-report-box">
            <div className="each-stu-r-b-top">
              <div>Total percentage</div>
              <div>100.0</div>
            </div>
            <div className="each-stu-r-b-top">
              <div> Obtained Percentage</div>
              <div>{student?.obt_percentage}</div>
            </div>
          </div>
          <div
            className="each-stu-report-box"
            style={{
              width: "40%",
              backgroundColor: "#01A7DE",
              color: "#FFFFFF",
            }}
          >
            <div className="each-stu-r-b-top" style={{ color: "#FFFFFF" }}>
              <div>Obtained marks</div>
              <div>{Number(student?.obt_marks)}</div>
            </div>
            <div className="each-stu-r-b-top" style={{ color: "#FFFFFF" }}>
              <div>Obtained percentage</div>
              <div>{student?.obt_percentage}</div>
            </div>
          </div>
        </div>
        <div className="each-stu-footer">
          <div>
            Teacher Remark &nbsp;
            <span
              className="pe-cursor"
              onMouseDown={() => setIsEditing(true)}
              onClick={() => editTeacherRemarksRef.current.focus()}
            >
              <PencilIcon size={14}  />
            </span>
          </div>

          <div
            style={{
              
              fontWeight: "400px",
              color: "#5B5A64",
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}
          >
            {/* text area to add teacher remarks */}
            <textarea
              ref={editTeacherRemarksRef}
              value={teacherRemarks}
              placeholder="Enter your remarks"
              onChange={(e) => setTeacherRemarks(e.target.value)}
              rows={2}
              cols={50}
              style={{ width: "90%", border: "none", resize: "none" }}
              disabled={isEditing ? false : true}
              
            />
          </div>
          <button
            className="my-btn"
            style={{ width: "fit-content"}}
            onClick={() => handleAddTeacherRemarks()}
          >
            save remarks
          </button>
        </div>
      </div>
      )}
    </div>
  );
};

export default EachStudentReport;
